import { Grid } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import useSyndicates from '../syndicates/useSyndicates';
import { Syndicate } from '../models/syndicate';
import useShallowRounds from '../rounds/useShallowRounds';
import { getNumFixtures } from '../common/roundhelpers';
import TerminalSyndicateCarousel from './terminalsyndicatecarousel';
import dbet_logo from '../images/dbet_logo.png';
import supertipset_logo from '../images/supertipset_logo.png';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// The syndicate card has a fixed design width, so it is scaled down to fit the terminal screen.
const CARD_WIDTH = 946;
const VIEWPORT_PADDING = 20;

export default function TerminalDisplay({ agentId }: { agentId: number }) {
  
  const syndicates = useSyndicates();
  const shallowRounds = useShallowRounds(true, false);

  const viewportRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardScale, setCardScale] = useState(1);
  const [cardHeight, setCardHeight] = useState(0);

  let list: Syndicate[] = [];

  if (syndicates[0] !== undefined) {
    list = syndicates[0].filter(s => s.agentId === agentId);
  }

  list.sort((a, b) => {
    let roundA = shallowRounds[0]?.rounds?.find(r => r.id === a.roundId.toString());
    let roundB = shallowRounds[0]?.rounds?.find(r => r.id === b.roundId.toString());

    if (!roundA || !roundB) return 0; // If either round missing, keep order

    // Convert openTo to Date (default to now if missing)
    const dateA = new Date(roundA.openTo ?? Date.now());
    const dateB = new Date(roundB.openTo ?? Date.now());

    // --- Adjust "day boundary" to 9 AM ---
    const shiftMs = 9 * 60 * 60 * 1000; // 9 hours in ms
    const shiftedA = new Date(dateA.getTime() - shiftMs);
    const shiftedB = new Date(dateB.getTime() - shiftMs);

    // Get date only, after shifting
    const aDateOnly = new Date(shiftedA.getFullYear(), shiftedA.getMonth(), shiftedA.getDate()).getTime();
    const bDateOnly = new Date(shiftedB.getFullYear(), shiftedB.getMonth(), shiftedB.getDate()).getTime();

    // Compare by "day" (with 9 AM as boundary)
    if (aDateOnly !== bDateOnly) return aDateOnly - bDateOnly;

    // If same adjusted day, compare by fixture count
    const fixturesA = getNumFixtures(roundA.name);
    const fixturesB = getNumFixtures(roundB.name);
    if (fixturesA !== fixturesB) {
      return fixturesB - fixturesA;
    }

    // If same fixture count, compare by actual time of day
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime();
    }

    // If same round, compare by stake amount
    if (roundA.id === roundB.id) {
      return Number(b.stakePerShare.amount) - Number(a.stakePerShare.amount);
    }

    return 0; // Equal for sorting purposes
  });

  list = list.sort((a, b) => {
    const diff1 = a.targetNShares - a.nShares;
    const diff2 = b.targetNShares - b.nShares;

    // Objects where a - b = 0 should come last
    if (diff1 === 0 && diff2 !== 0) return 1;
    if (diff2 === 0 && diff1 !== 0) return -1;
    return 0; // Keep relative order for the rest
  })

  // Apply filter based on selectedFilter
  let filteredList = list.filter((syndicate) => {
    const round = shallowRounds[0]?.rounds?.find(r => r.id === syndicate.roundId.toString() &&
      r.status === "ACTIVE" && new Date(r.openTo) > new Date(Date.now()));

    if (!round) return false;
    return true;
  });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const card = cardRef.current;
    if (!viewport || !card) return;

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      // Deferred to the next frame so the observer never re-enters within the same delivery cycle.
      frame = requestAnimationFrame(() => {
        const height = card.scrollHeight;
        const availableWidth = viewport.clientWidth - 2 * VIEWPORT_PADDING;
        const availableHeight = viewport.clientHeight - 2 * VIEWPORT_PADDING;
        setCardHeight(height);
        setCardScale(Math.min(1,
          availableWidth / CARD_WIDTH,
          height > 0 ? availableHeight / height : 1));
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    observer.observe(card);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [filteredList.length]);

  if (shallowRounds[0] === undefined)
    return <></>;

  const sliderSettings = {
    dots: false,
    infinite: filteredList.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: filteredList.length > 1,
    autoplaySpeed: 10000,
    arrows: false,
    cenntermode: true,
    cennterpadding: '0px',
    cssEase: 'ease-in-out',
    variableWidth: true,
  };

  return (

    <Grid container direction="row" sx={{
      backgroundColor: '#061D4D',
      width: "100%",
      height: "100vh",
      overflow: "hidden"
    }}>

      {/* Left Column */}
      <Grid item sx={{
        width: "25%",
        minWidth: "300px",
        maxWidth: "480px",
        height: "100%",
        backgroundColor: "#031535",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "40px"
      }}>
        <img src={dbet_logo} alt="Dbet Logo" style={{ maxWidth: "80%", height: "auto" }} />
        <img src={supertipset_logo} alt="Supertipset Logo" style={{ maxWidth: "80%", height: "auto" }} />
      </Grid>

      {/* Right Column - Syndicates List */}
      <Grid item ref={viewportRef} sx={{
        flexGrow: 1,
        minWidth: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        padding: `${VIEWPORT_PADDING}px`,
        backgroundColor: "#061D4D",
        overflow: "hidden"
      }}>
        {filteredList.length === 0 ? (
          <div style={{ color: '#000000', fontSize: '24px' }}>No syndicates available</div>
        ) : (
          <div style={{
            width: CARD_WIDTH * cardScale,
            height: cardHeight * cardScale
          }}>
            <div ref={cardRef} style={{
              width: CARD_WIDTH,
              transform: `scale(${cardScale})`,
              transformOrigin: 'top left'
            }}>
              <Slider {...sliderSettings}>
                {filteredList.map((syndicate, i) => {
                  let round = shallowRounds[0]?.rounds?.find(r => r.id === syndicate.roundId.toString());
                  return (
                    <div key={"syndicate" + i}>
                      <TerminalSyndicateCarousel slimSyndicate={syndicate} round={round!} />
                    </div>
                  );
                })}
              </Slider>
            </div>
          </div>
        )}
      </Grid>

    </Grid >
  );
}
